import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { callCheckOnetimePasswordApi, CheckOnetimePasswordResData } from 'requests/RequestUser';
import { RoutePath } from 'consts/RoutePath';
import { useStore } from 'store/StoreContext';
import { CircularProgress } from '@material-ui/core';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { ApiError } from 'requests/RequestBase';
import { ApiErrorCode, findErrorMessageByApiError } from 'consts/ApiErrorCode';
import { convertObjectToGetParameterWithEncode } from 'common/ObjectToGetParameterConverter';

type Props = {};

export const ForgotPasswordConfirmTemplate = memo((_props: Props) => {
	const history = useHistory();
	const [store] = useStore();
	const [formData, setFormData] = useState({ onetime_password: '' });
	const [formErrors, setFormErrors] = useState({ onetime_password: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [processLoading, setProcessLoading] = useState(false);

	// ワンタイムパスワード入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, onetime_password: event.target.value }));
	};

	// 確認ボタンのonClickで呼ぶ
	const checkOnetimePassword = async () => {
		setProcessLoading(true);

		// エラー解除
		setFormErrors((prev) => ({ ...prev, onetime_password: '' }));

		// ワンタイムパスワード検証
		if (!formData.onetime_password.trim()) {
			setFormErrors((prev) => ({ ...prev, onetime_password: ReactError.E0001 }));
			setProcessLoading(false);
			return;
		}
		const checkOnetimePasswordResult = await callCheckOnetimePasswordApi({
			mailaddress: store.forgotPasswordData.mailaddress,
			onetimepass: formData.onetime_password,
		});
		if (checkOnetimePasswordResult.status !== HttpStatus.OK) {
			const error = checkOnetimePasswordResult.data as ApiError;
			if (error.error_code === ApiErrorCode.E1157.code) {
				setFormErrors((prev) => ({ ...prev, onetime_password: findErrorMessageByApiError(error) }));
				setProcessLoading(false);
				return;
			}
			history.push(RoutePath.ERROR);
			return;
		}
		const data = checkOnetimePasswordResult.data as CheckOnetimePasswordResData;
		if (!data.exist) {
			setFormErrors((prev) => ({ ...prev, onetime_password: 'ワンタイムパスワードが正しくありません。' }));
			setProcessLoading(false);
			return;
		}

		// 次のページへ
		history.push({ pathname: RoutePath.FORGOT_PASSWORD_UPDATE, state: { confirmGid: data.gid } });
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenIDパスワード忘れ｜認証コード入力';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors]);

	return (
		<>
			<section className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="section-head">パスワード忘れ</div>
					<div className="section-txt-box">
						<div className="txt-head">メール受信の確認</div>
						<p>
							ご指定のメールアドレスに、メールをお送りいたしました。
							<br />
							メールに記載されたワンタイムパスワードを入力してください。
						</p>
					</div>

					{isFormError ? (
						<div className="error-box" ref={errorRef}>
							<>
								<p>※下記項目は全て必須となります。</p>
								<div className="error-box-in">
									{formErrors.onetime_password !== '' ? (
										<p>【ワンタイムパスワード】{formErrors.onetime_password}</p>
									) : null}
								</div>
							</>
						</div>
					) : null}

					<div className="form-box">
						<div className="form-head">
							<label htmlFor="">ワンタイムパスワード</label>
						</div>
						<div className="form-input">
							<input
								type="password"
								name="onetime_password"
								value={formData.onetime_password}
								onChange={handleChangeValue}
							/>
						</div>
						<p className="error">{formErrors.onetime_password}</p>
					</div>
					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? <CircularProgress /> : <button onClick={checkOnetimePassword}>確認</button>}
						</div>
					</div>
				</div>
			</section>
		</>
	);
});
